/**
 * Labstep
 *
 * @module prosemirror/components/Menu/Commands/Menu
 * @desc A menu to list elements
 */

import { Ref } from 'labstep-web/core/Ref';
import { MenuCommandsSection } from 'labstep-web/prosemirror/components/Menu/Commands';
import { MenuCommandsMenuElement } from 'labstep-web/prosemirror/components/Menu/Commands/Menu/Element';
import React, {
  type ForwardRefRenderFunction,
  forwardRef,
} from 'react';
import { Menu } from 'semantic-ui-react';
import type { IMenuCommandsMenuProps } from './types';

const MenuCommandsMenuInner: ForwardRefRenderFunction<
  HTMLElement,
  IMenuCommandsMenuProps
> = (
  {
    state,
    dispatch,
    elements,
    index,
    activeItemRef,
    topLevelPermissionEntity,
  },
  ref,
) => {
  // Create a flattened list with section headers
  const flattenedElements = React.useMemo(() => {
    // Group elements by section
    const groups: {
      [key in MenuCommandsSection]?: Array<{
        element: any;
        originalIndex: number;
      }>;
    } = {};

    elements.forEach((element, i) => {
      const section = element.section || MenuCommandsSection.Common;
      if (!groups[section]) {
        groups[section] = [];
      }
      groups[section]!.push({ element, originalIndex: i });
    });

    // Define section order
    const sectionOrder = [
      MenuCommandsSection.Common,
      MenuCommandsSection.Tables,
      MenuCommandsSection.InputFields,
      MenuCommandsSection.OutputFields,
      MenuCommandsSection.DomainTools,
      MenuCommandsSection.Code,
    ];

    // Flatten with headers - use sequential indices that match the sorted getAvailableElements order
    const flattened: Array<{
      type: 'header' | 'element';
      section?: MenuCommandsSection;
      element?: any;
      elementIndex?: number;
    }> = [];
    let elementIndex = 0;

    sectionOrder.forEach((sectionKey, sectionIndex) => {
      const sectionElements = groups[sectionKey];
      if (!sectionElements || sectionElements.length === 0) {
        return;
      }

      if (sectionIndex > 0 && flattened.length > 0) {
        flattened.push({ type: 'header', section: undefined }); // divider
      }

      flattened.push({ type: 'header', section: sectionKey });

      sectionElements.forEach(({ element }) => {
        flattened.push({
          type: 'element',
          element,
          elementIndex: elementIndex++,
        });
      });
    });

    return flattened;
  }, [elements]);

  return (
    <Ref innerRef={ref}>
      <Menu vertical borderless fluid size="mini">
        {elements.length === 0 ? (
          <Menu.Item>No results</Menu.Item>
        ) : (
          flattenedElements.map((item, i) => {
            if (item.type === 'header') {
              if (item.section) {
                return (
                  <Menu.Item key={`header-${item.section}`} header>
                    {item.section}
                  </Menu.Item>
                );
              } else {
                return <div></div>;
              }
            } else {
              return (
                <MenuCommandsMenuElement
                  key={item.element!.id}
                  element={item.element!}
                  state={state}
                  dispatch={dispatch}
                  index={index}
                  activeItemRef={activeItemRef}
                  i={item.elementIndex!}
                  topLevelPermissionEntity={topLevelPermissionEntity}
                />
              );
            }
          })
        )}
      </Menu>
    </Ref>
  );
};

export const MenuCommandsMenu = forwardRef(MenuCommandsMenuInner);
